import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import AdminHeader from '../components/AdminHeader';
import { User, UserPlus, UserMinus, AlertCircle, Search, Mail, Lock, Shield } from 'lucide-react';

interface UserData {
  id: string;
  email: string;
  user_metadata: {
    name?: string;
    role?: string;
  };
  created_at: string;
}

export default function AdminUsers() {
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [processing, setProcessing] = useState<string | null>(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  async function fetchUsers() {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Não autorizado');

      const { data, error: fetchError } = await supabase
        .from('users')
        .select('*')
        .order('email');

      if (fetchError) throw fetchError;
      setUsers(data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handlePromoteToAdmin(userId: string) {
    try {
      setProcessing(userId);
      const { error } = await supabase.rpc('promote_to_admin', {
        target_user_id: userId
      });
      
      if (error) throw error;
      await fetchUsers();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setProcessing(null);
    }
  }

  async function handleDemoteFromAdmin(userId: string) {
    try {
      setProcessing(userId);
      const { error } = await supabase.rpc('demote_from_admin', {
        target_user_id: userId
      });
      
      if (error) throw error;
      await fetchUsers();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setProcessing(null);
    }
  }

  const filteredUsers = users.filter(user => 
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.user_metadata?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <AdminHeader title="Gerenciar Usuários" icon={User} />
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <AdminHeader title="Gerenciar Usuários" icon={User} />

      {error && (
        <div className="mb-4 p-4 bg-red-50 text-red-600 rounded-lg flex items-start">
          <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar usuários..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Usuário</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Email</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Função</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id} className="border-b border-gray-200">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-brand-light text-brand-teal flex items-center justify-center font-medium">
                        {user.user_metadata?.name?.[0]?.toUpperCase() || user.email[0].toUpperCase()}
                      </div>
                      <span>{user.user_metadata?.name || 'Sem nome'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{user.email}</td>
                  <td className="px-4 py-3">
                    {user.user_metadata?.role === 'admin' ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-brand-light text-brand-teal">
                        Administrador
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                        Usuário
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {user.user_metadata?.role === 'admin' ? (
                      <button
                        onClick={() => handleDemoteFromAdmin(user.id)}
                        disabled={processing === user.id}
                        className="text-red-500 hover:text-red-600 p-1"
                        title="Remover privilégios de administrador"
                      >
                        <UserMinus className="h-5 w-5" />
                      </button>
                    ) : (
                      <button
                        onClick={() => handlePromoteToAdmin(user.id)}
                        disabled={processing === user.id}
                        className="text-brand-teal hover:text-brand-teal/80 p-1"
                        title="Promover a administrador"
                      >
                        <Shield className="h-5 w-5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                    Nenhum usuário encontrado
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}